import React, { useRef, useEffect, useState } from "react";
import { Trash2, Eraser, Pen, CheckCircle, Loader2 } from "lucide-react";
import { Box, Button, Typography, Paper, IconButton } from "@mui/material";

interface ScribblePadProps {
    onSave?: (dataUrl: string) => void;
    onRecognized?: (text: string) => void;
}

const ScribblePad: React.FC<ScribblePadProps> = ({ onSave, onRecognized }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState("#000000");
    const [lineWidth, setLineWidth] = useState(3);
    const [mode, setMode] = useState<"draw" | "erase">("draw");

    // Recognition states
    type Stroke = [number[], number[], number[]];
    const [strokes, setStrokes] = useState<Stroke[]>([]);
    const [currentStroke, setCurrentStroke] = useState<{ x: number[], y: number[], t: number[] }>({ x: [], y: [], t: [] });
    const [isRecognizing, setIsRecognizing] = useState(false);
    const [recognizedText, setRecognizedText] = useState("");

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const resizeCanvas = () => {
            const container = canvas.parentElement;
            if (container) {
                const { width, height } = container.getBoundingClientRect();
                canvas.width = width;
                canvas.height = height;
                ctx.lineCap = "round";
                ctx.lineJoin = "round";
            }
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);
        return () => window.removeEventListener("resize", resizeCanvas);
    }, []);

    const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
        setIsDrawing(true);
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        let x: number, y: number;
        if ("touches" in e) {
            x = e.touches[0].clientX - rect.left;
            y = e.touches[0].clientY - rect.top;
        } else {
            x = (e as React.MouseEvent).clientX - rect.left;
            y = (e as React.MouseEvent).clientY - rect.top;
        }

        setCurrentStroke({ x: [x], y: [y], t: [Date.now()] });

        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(x, y);
        }
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);

        if (currentStroke.x.length > 0) {
            setStrokes(prev => [...prev, [currentStroke.x, currentStroke.y, currentStroke.t]]);
        }

        const canvas = canvasRef.current;
        if (canvas && onSave) {
            onSave(canvas.toDataURL());
        }
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = mode === "erase" ? "#ffffff" : color;
        ctx.globalCompositeOperation = mode === "erase" ? "destination-out" : "source-over";

        const rect = canvas.getBoundingClientRect();
        let x: number, y: number;

        if ("touches" in e) {
            x = e.touches[0].clientX - rect.left;
            y = e.touches[0].clientY - rect.top;
        } else {
            x = (e as React.MouseEvent).clientX - rect.left;
            y = (e as React.MouseEvent).clientY - rect.top;
        }

        ctx.lineTo(x, y);
        ctx.stroke();

        setCurrentStroke(prev => ({
            x: [...prev.x, x],
            y: [...prev.y, y],
            t: [...prev.t, Date.now()]
        }));
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setStrokes([]);
            setRecognizedText("");
        }
    };

    const handleRecognize = async () => {
        if (strokes.length === 0) return;

        setIsRecognizing(true);
        try {
            const canvas = canvasRef.current;
            if (!canvas) return;

            // Try multiple recognition approaches for better math/formula detection
            const recognitionResults = await Promise.allSettled([
                recognizeWithGoogle(strokes, canvas),
                recognizeWithMathPattern(strokes, canvas),
                recognizeWithPowerPattern(strokes, canvas)
            ]);

            // Combine results and find best match
            const bestResult = combineRecognitionResults(recognitionResults);
            if (bestResult) {
                setRecognizedText(bestResult);
                if (onRecognized) onRecognized(bestResult);
            }
        } catch (error) {
            console.error("Recognition failed:", error);
        } finally {
            setIsRecognizing(false);
        }
    };

    const recognizeWithGoogle = async (strokes: Stroke[], canvas: HTMLCanvasElement) => {
        try {
            const response = await fetch("https://www.google.com.tw/inputtools/request?ime=handwriting&app=autodraw&dbg=1&cs=1&oe=UTF-8", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    options: "enable_pre_space",
                    requests: [{
                        writing_guide: {
                            writing_area_width: canvas.width,
                            writing_area_height: canvas.height,
                        },
                        ink: strokes,
                        language: "en",
                    }],
                }),
            });

            const data = await response.json();
            if (data && data[0] === "SUCCESS") {
                const results = data[1][0][1];
                if (results && results.length > 0) {
                    return { source: 'google', text: results[0], confidence: 0.8 };
                }
            }
        } catch (error) {
            console.error("Google recognition failed:", error);
        }
        return null;
    };

    const recognizeWithMathPattern = (strokes: Stroke[], canvas: HTMLCanvasElement) => {
        // Analyze stroke patterns for common math symbols
        const patterns = analyzeMathPatterns(strokes);
        const matches = [];

        // Check for common math symbols based on stroke patterns
        if (patterns.hasCircle) matches.push({ symbol: '○', confidence: 0.7 });
        if (patterns.hasVerticalLine) matches.push({ symbol: '|', confidence: 0.6 });
        if (patterns.hasHorizontalLine) matches.push({ symbol: '―', confidence: 0.6 });
        if (patterns.hasPlus) matches.push({ symbol: '+', confidence: 0.8 });
        if (patterns.hasMinus) matches.push({ symbol: '-', confidence: 0.8 });
        if (patterns.hasMultiply) matches.push({ symbol: '×', confidence: 0.7 });
        if (patterns.hasDivide) matches.push({ symbol: '÷', confidence: 0.7 });
        if (patterns.hasEqual) matches.push({ symbol: '=', confidence: 0.8 });
        if (patterns.hasSqrt) matches.push({ symbol: '√', confidence: 0.7 });
        if (patterns.hasIntegral) matches.push({ symbol: '∫', confidence: 0.7 });
        if (patterns.hasSum) matches.push({ symbol: '∑', confidence: 0.7 });
        if (patterns.hasPi) matches.push({ symbol: 'π', confidence: 0.7 });
        if (patterns.hasAlpha) matches.push({ symbol: 'α', confidence: 0.6 });
        if (patterns.hasBeta) matches.push({ symbol: 'β', confidence: 0.6 });
        if (patterns.hasDelta) matches.push({ symbol: 'Δ', confidence: 0.6 });
        if (patterns.hasTheta) matches.push({ symbol: 'θ', confidence: 0.6 });
        if (patterns.hasLambda) matches.push({ symbol: 'λ', confidence: 0.6 });
        if (patterns.hasSigma) matches.push({ symbol: 'σ', confidence: 0.6 });
        if (patterns.hasPhi) matches.push({ symbol: 'φ', confidence: 0.6 });
        if (patterns.hasOmega) matches.push({ symbol: 'ω', confidence: 0.6 });

        // Check for power/exponent patterns (superscript positioning)
        const powerResult = detectPowerPattern(strokes);
        if (powerResult) {
            matches.push({ symbol: powerResult, confidence: 0.8 });
        }

        if (matches.length > 0) {
            const best = matches.reduce((prev, current) =>
                current.confidence > prev.confidence ? current : prev
            );
            return { source: 'math-pattern', text: best.symbol, confidence: best.confidence };
        }

        return null;
    };

    const recognizeWithPowerPattern = (strokes: Stroke[], canvas: HTMLCanvasElement) => {
        // Specific detection for power values and exponents
        const powerPattern = detectPowerPattern(strokes);
        if (powerPattern) {
            return { source: 'power-pattern', text: powerPattern, confidence: 0.9 };
        }
        return null;
    };

    const analyzeMathPatterns = (strokes: Stroke[]) => {
        const patterns: Record<string, boolean> = {
            hasCircle: false,
            hasVerticalLine: false,
            hasHorizontalLine: false,
            hasPlus: false,
            hasMinus: false,
            hasMultiply: false,
            hasDivide: false,
            hasEqual: false,
            hasSqrt: false,
            hasIntegral: false,
            hasSum: false,
            hasPi: false,
            hasAlpha: false,
            hasBeta: false,
            hasDelta: false,
            hasTheta: false,
            hasLambda: false,
            hasSigma: false,
            hasPhi: false,
            hasOmega: false,
        };

        // Analyze overall stroke characteristics first
        const strokeCount = strokes.length;
        const totalPoints = strokes.reduce((sum, stroke) => sum + stroke[0].length, 0);
        const bounds = calculateStrokeBounds(strokes);
        const aspectRatio = bounds.width / bounds.height;
        const complexity = calculateStrokeComplexity(strokes);

        strokes.forEach((stroke, index) => {
            const [xPoints, yPoints] = stroke;
            if (xPoints.length < 2) return;

            // More precise geometric analysis
            const strokeLength = calculateStrokeLength(xPoints, yPoints);
            const strokeDirection = calculateDominantDirection(xPoints, yPoints);
            const curvature = calculateCurvature(xPoints, yPoints);

            // Detect basic shapes with more specific criteria
            if (isCirclePrecise(xPoints, yPoints, strokeLength)) {
                patterns.hasCircle = true;
            }
            if (isVerticalLinePrecise(xPoints, yPoints, strokeDirection)) {
                patterns.hasVerticalLine = true;
            }
            if (isHorizontalLinePrecise(xPoints, yPoints, strokeDirection)) {
                patterns.hasHorizontalLine = true;
            }

            // Multi-stroke pattern detection with better spatial analysis
            if (index < strokes.length - 1) {
                const nextStroke = strokes[index + 1];
                const [nextX, nextY] = nextStroke;

                if (nextX.length > 0) {
                    const spatialRelation = analyzeSpatialRelation(stroke, nextStroke);

                    // Plus sign detection
                    if (spatialRelation.isCrossing &&
                        (isHorizontalLinePrecise(xPoints, yPoints, strokeDirection) || isVerticalLinePrecise(xPoints, yPoints, strokeDirection))) {
                        patterns.hasPlus = true;
                    }

                    // Equals sign detection
                    if (spatialRelation.isParallel &&
                        isHorizontalLinePrecise(xPoints, yPoints, strokeDirection) &&
                        isHorizontalLinePrecise(nextX, nextY, calculateDominantDirection(nextX, nextY)) &&
                        spatialRelation.verticalDistance < 20) {
                        patterns.hasEqual = true;
                    }

                    // Multiply sign detection
                    if (spatialRelation.isCrossing && spatialRelation.angleNear45) {
                        patterns.hasMultiply = true;
                    }
                }
            }

            // Complex symbol detection based on stroke count and overall characteristics
            if (strokeCount === 1) {
                // Single stroke analysis
                if (isSqrtPrecise(xPoints, yPoints, curvature, aspectRatio)) {
                    patterns.hasSqrt = true;
                }
                if (isIntegralPrecise(xPoints, yPoints, strokeLength, curvature)) {
                    patterns.hasIntegral = true;
                }
                if (isSumPrecise(xPoints, yPoints, complexity)) {
                    patterns.hasSum = true;
                }
                if (isPiPrecise(xPoints, yPoints, aspectRatio, curvature)) {
                    patterns.hasPi = true;
                }
                if (isAlphaPrecise(xPoints, yPoints, aspectRatio)) {
                    patterns.hasAlpha = true;
                }
                if (isBetaPrecise(xPoints, yPoints, complexity)) {
                    patterns.hasBeta = true;
                }
                if (isDeltaPrecise(xPoints, yPoints, aspectRatio)) {
                    patterns.hasDelta = true;
                }
                if (isThetaPrecise(xPoints, yPoints, aspectRatio)) {
                    patterns.hasTheta = true;
                }
                if (isLambdaPrecise(xPoints, yPoints, aspectRatio)) {
                    patterns.hasLambda = true;
                }
                if (isSigmaPrecise(xPoints, yPoints, complexity)) {
                    patterns.hasSigma = true;
                }
                if (isPhiPrecise(xPoints, yPoints, aspectRatio)) {
                    patterns.hasPhi = true;
                }
                if (isOmegaPrecise(xPoints, yPoints, complexity)) {
                    patterns.hasOmega = true;
                }
            }
        });

        return patterns;
    };

    // Helper functions for more precise analysis
    const calculateStrokeBounds = (strokes: Stroke[]) => {
        const allX = strokes.flatMap(s => s[0]);
        const allY = strokes.flatMap(s => s[1]);
        return {
            minX: Math.min(...allX),
            maxX: Math.max(...allX),
            minY: Math.min(...allY),
            maxY: Math.max(...allY),
            width: Math.max(...allX) - Math.min(...allX),
            height: Math.max(...allY) - Math.min(...allY)
        };
    };

    const calculateStrokeComplexity = (strokes: Stroke[]) => {
        let totalChanges = 0;
        strokes.forEach(stroke => {
            const [xPoints, yPoints] = stroke;
            for (let i = 1; i < xPoints.length - 1; i++) {
                const dir1 = Math.atan2(yPoints[i] - yPoints[i - 1], xPoints[i] - xPoints[i - 1]);
                const dir2 = Math.atan2(yPoints[i + 1] - yPoints[i], xPoints[i + 1] - xPoints[i]);
                if (Math.abs(dir1 - dir2) > Math.PI / 6) {
                    totalChanges++;
                }
            }
        });
        return totalChanges;
    };

    const calculateStrokeLength = (xPoints: number[], yPoints: number[]) => {
        let length = 0;
        for (let i = 1; i < xPoints.length; i++) {
            length += Math.sqrt(Math.pow(xPoints[i] - xPoints[i - 1], 2) + Math.pow(yPoints[i] - yPoints[i - 1], 2));
        }
        return length;
    };

    const calculateDominantDirection = (xPoints: number[], yPoints: number[]) => {
        if (xPoints.length < 2) return 0;
        const dx = xPoints[xPoints.length - 1] - xPoints[0];
        const dy = yPoints[yPoints.length - 1] - yPoints[0];
        return Math.atan2(dy, dx);
    };

    const calculateCurvature = (xPoints: number[], yPoints: number[]) => {
        if (xPoints.length < 3) return 0;
        let totalCurvature = 0;
        for (let i = 1; i < xPoints.length - 1; i++) {
            const angle1 = Math.atan2(yPoints[i] - yPoints[i - 1], xPoints[i] - xPoints[i - 1]);
            const angle2 = Math.atan2(yPoints[i + 1] - yPoints[i], xPoints[i + 1] - xPoints[i]);
            totalCurvature += Math.abs(angle2 - angle1);
        }
        return totalCurvature / (xPoints.length - 1);
    };

    const analyzeSpatialRelation = (stroke1: Stroke, stroke2: Stroke) => {
        const [x1, y1] = stroke1;
        const [x2, y2] = stroke2;

        const center1 = { x: x1.reduce((a, b) => a + b, 0) / x1.length, y: y1.reduce((a, b) => a + b, 0) / y1.length };
        const center2 = { x: x2.reduce((a, b) => a + b, 0) / x2.length, y: y2.reduce((a, b) => a + b, 0) / y2.length };

        const distance = Math.sqrt(Math.pow(center2.x - center1.x, 2) + Math.pow(center2.y - center1.y, 2));
        const angle = Math.atan2(center2.y - center1.y, center2.x - center1.x);

        return {
            isCrossing: distance < 30,
            isParallel: Math.abs(angle) < Math.PI / 8 || Math.abs(angle - Math.PI) < Math.PI / 8,
            verticalDistance: Math.abs(center2.y - center1.y),
            horizontalDistance: Math.abs(center2.x - center1.x),
            angleNear45: Math.abs(angle - Math.PI / 4) < Math.PI / 8
        };
    };

    // More precise pattern detection functions
    const isCirclePrecise = (xPoints: number[], yPoints: number[], strokeLength: number) => {
        if (xPoints.length < 8) return false;
        const firstPoint = { x: xPoints[0], y: yPoints[0] };
        const lastPoint = { x: xPoints[xPoints.length - 1], y: yPoints[yPoints.length - 1] };
        const distance = Math.sqrt(Math.pow(lastPoint.x - firstPoint.x, 2) + Math.pow(lastPoint.y - firstPoint.y, 2));
        const expectedCircleLength = 2 * Math.PI * distance / 2;
        return distance < 25 && Math.abs(strokeLength - expectedCircleLength) < expectedCircleLength * 0.3;
    };

    const isVerticalLinePrecise = (xPoints: number[], yPoints: number[], direction: number) => {
        if (xPoints.length < 3) return false;
        const xVariance = calculateVariance(xPoints);
        const yRange = Math.max(...yPoints) - Math.min(...yPoints);
        const isVertical = Math.abs(direction - Math.PI / 2) < Math.PI / 4;
        return xVariance < 15 && yRange > 25 && isVertical;
    };

    const isHorizontalLinePrecise = (xPoints: number[], yPoints: number[], direction: number) => {
        if (xPoints.length < 3) return false;
        const yVariance = calculateVariance(yPoints);
        const xRange = Math.max(...xPoints) - Math.min(...xPoints);
        const isHorizontal = Math.abs(direction) < Math.PI / 4 || Math.abs(direction - Math.PI) < Math.PI / 4;
        return yVariance < 15 && xRange > 25 && isHorizontal;
    };

    const isSqrtPrecise = (xPoints: number[], yPoints: number[], curvature: number, aspectRatio: number) => {
        return xPoints.length > 10 && curvature > 0.5 && aspectRatio > 0.8;
    };

    const isIntegralPrecise = (xPoints: number[], yPoints: number[], strokeLength: number, curvature: number) => {
        return strokeLength > 30 && curvature > 0.3 && yPoints[yPoints.length - 1] > yPoints[0] + 20;
    };

    const isSumPrecise = (xPoints: number[], yPoints: number[], complexity: number) => {
        return complexity > 3 && xPoints.length > 15;
    };

    const isPiPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number, curvature: number) => {
        return aspectRatio > 0.6 && aspectRatio < 1.5 && curvature > 0.4;
    };

    const isAlphaPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number) => {
        return aspectRatio > 0.4 && aspectRatio < 1.2;
    };

    const isBetaPrecise = (xPoints: number[], yPoints: number[], complexity: number) => {
        return complexity > 2 && xPoints.length > 12;
    };

    const isDeltaPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number) => {
        return aspectRatio > 0.8 && aspectRatio < 1.3;
    };

    const isThetaPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number) => {
        return aspectRatio > 0.7 && aspectRatio < 1.4;
    };

    const isLambdaPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number) => {
        return aspectRatio > 0.5 && aspectRatio < 1.0;
    };

    const isSigmaPrecise = (xPoints: number[], yPoints: number[], complexity: number) => {
        return complexity > 4 && xPoints.length > 20;
    };

    const isPhiPrecise = (xPoints: number[], yPoints: number[], aspectRatio: number) => {
        return aspectRatio > 0.6 && aspectRatio < 1.3;
    };

    const isOmegaPrecise = (xPoints: number[], yPoints: number[], complexity: number) => {
        return complexity > 5 && xPoints.length > 25;
    };

    const detectPowerPattern = (strokes: Stroke[]) => {
        if (strokes.length < 2) return null;

        // More sophisticated power detection
        const bounds = calculateStrokeBounds(strokes);
        const strokeAnalysis = strokes.map(stroke => ({
            avgY: stroke[1].reduce((sum, y) => sum + y, 0) / stroke[1].length,
            avgX: stroke[0].reduce((sum, x) => sum + x, 0) / stroke[0].length,
            length: calculateStrokeLength(stroke[0], stroke[1])
        }));

        const overallAvgY = strokeAnalysis.reduce((sum, stroke) => sum + stroke.avgY, 0) / strokeAnalysis.length;
        const baselineY = Math.max(...strokeAnalysis.map(s => s.avgY));

        // Classify strokes based on vertical position relative to baseline
        const baseStrokes: Stroke[] = [];
        const exponentStrokes: Stroke[] = [];
        const subscriptStrokes: Stroke[] = [];

        strokeAnalysis.forEach((analysis, index) => {
            const stroke = strokes[index];
            const relativePosition = baselineY - analysis.avgY;

            // More precise classification based on position and size
            if (relativePosition > 25 && analysis.length < 40) {
                exponentStrokes.push(stroke);
            } else if (relativePosition < -15 && analysis.length < 30) {
                subscriptStrokes.push(stroke);
            } else {
                baseStrokes.push(stroke);
            }
        });

        // Try different combinations
        const results = [];

        // Base + Exponent pattern
        if (baseStrokes.length > 0 && exponentStrokes.length > 0) {
            const baseText = identifySimpleText(baseStrokes);
            const exponentText = identifySimpleText(exponentStrokes);
            if (baseText && exponentText) {
                results.push({ text: `${baseText}^${exponentText}`, confidence: 0.9, type: 'power' });
            }
        }

        // Base + Subscript pattern
        if (baseStrokes.length > 0 && subscriptStrokes.length > 0) {
            const baseText = identifySimpleText(baseStrokes);
            const subscriptText = identifySimpleText(subscriptStrokes);
            if (baseText && subscriptText) {
                results.push({ text: `${baseText}_${subscriptText}`, confidence: 0.8, type: 'subscript' });
            }
        }

        // Check for fraction pattern (horizontal line with strokes above and below)
        const hasHorizontalLine = strokeAnalysis.some(analysis =>
            Math.abs(analysis.avgY - baselineY) < 10 &&
            analysis.length > bounds.width * 0.6
        );

        if (hasHorizontalLine) {
            const aboveStrokes = strokes.filter(stroke => {
                const avgY = stroke[1].reduce((sum, y) => sum + y, 0) / stroke[1].length;
                return avgY < baselineY - 15;
            });

            const belowStrokes = strokes.filter(stroke => {
                const avgY = stroke[1].reduce((sum, y) => sum + y, 0) / stroke[1].length;
                return avgY > baselineY + 15;
            });

            if (aboveStrokes.length > 0 && belowStrokes.length > 0) {
                const numeratorText = identifySimpleText(aboveStrokes);
                const denominatorText = identifySimpleText(belowStrokes);
                if (numeratorText && denominatorText) {
                    results.push({ text: `\\frac{${numeratorText}}{${denominatorText}}`, confidence: 0.85, type: 'fraction' });
                }
            }
        }

        // Return result with highest confidence
        if (results.length > 0) {
            results.sort((a, b) => b.confidence - a.confidence);
            return results[0].text;
        }

        return null;
    };

    const identifySimpleText = (strokes: Stroke[]) => {
        if (strokes.length === 0) return null;

        // Enhanced pattern recognition with more specific criteria
        const stroke = strokes[0];
        const [xPoints, yPoints] = stroke;

        // Calculate stroke characteristics
        const strokeLength = calculateStrokeLength(xPoints, yPoints);
        const bounds = calculateStrokeBounds(strokes);
        const aspectRatio = bounds.width / bounds.height;
        const complexity = calculateStrokeComplexity(strokes);
        const direction = calculateDominantDirection(xPoints, yPoints);

        // Number patterns with better specificity
        const numberPatterns = {
            '0': () => isCirclePrecise(xPoints, yPoints, strokeLength),
            '1': () => isVerticalLinePrecise(xPoints, yPoints, direction) && strokeLength < 60,
            '2': () => hasCurveAndLine(xPoints, yPoints) && aspectRatio > 0.8,
            '3': () => hasMultipleCurves(xPoints, yPoints) && complexity > 2,
            '4': () => hasLineAndAngle(xPoints, yPoints) && complexity > 1,
            '5': () => hasCurveAndLine(xPoints, yPoints) && aspectRatio < 1.2,
            '6': () => hasLoopAndTail(xPoints, yPoints) && complexity > 2,
            '7': () => isHorizontalLinePrecise(xPoints, yPoints, direction) && complexity > 1,
            '8': () => hasDoubleLoop(xPoints, yPoints) && complexity > 3,
            '9': () => hasLoopAndLine(xPoints, yPoints) && complexity > 2,
        };

        // Variable patterns
        const variablePatterns = {
            'x': () => hasCrossPattern(xPoints, yPoints) && complexity > 2,
            'y': () => hasCurveAndVertical(xPoints, yPoints) && complexity > 1,
            'z': () => hasHorizontalLineWithTail(xPoints, yPoints),
            'n': () => hasDownwardStrokeWithCurve(xPoints, yPoints),
            'm': () => hasMultiplePeaks(xPoints, yPoints),
            'a': () => hasCircleWithTail(xPoints, yPoints) && aspectRatio < 1.0,
            'b': () => hasVerticalLineWithLoops(xPoints, yPoints),
            'c': () => hasOpenCurve(xPoints, yPoints),
            'd': () => hasCircleWithVertical(xPoints, yPoints) && aspectRatio > 0.8,
        };

        // Test number patterns first
        for (const [char, pattern] of Object.entries(numberPatterns)) {
            if (pattern()) {
                return char;
            }
        }

        // Test variable patterns
        for (const [char, pattern] of Object.entries(variablePatterns)) {
            if (pattern()) {
                return char;
            }
        }

        return null;
    };

    // Additional helper functions for enhanced pattern detection
    const hasCurveAndVertical = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints.slice(0, -5), yPoints.slice(0, -5)) && isVerticalLinePrecise(xPoints.slice(-5), yPoints.slice(-5), calculateDominantDirection(xPoints.slice(-5), yPoints.slice(-5)));
    };

    const hasDownwardStrokeWithCurve = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints.slice(0, -3), yPoints.slice(0, -3)) && hasDownwardStroke(xPoints.slice(-3), yPoints.slice(-3));
    };

    const hasMultiplePeaks = (xPoints: number[], yPoints: number[]) => {
        let peaks = 0;
        for (let i = 1; i < xPoints.length - 1; i++) {
            if (yPoints[i] > yPoints[i - 1] && yPoints[i] > yPoints[i + 1]) {
                peaks++;
            }
        }
        return peaks >= 2;
    };

    const hasHorizontalLineWithTail = (xPoints: number[], yPoints: number[]) => {
        return isHorizontalLinePrecise(xPoints.slice(0, -5), yPoints.slice(0, -5), calculateDominantDirection(xPoints.slice(0, -5), yPoints.slice(0, -5))) &&
            xPoints.length > 8;
    };

    const hasVerticalLineWithLoops = (xPoints: number[], yPoints: number[]) => {
        return isVerticalLinePrecise(xPoints, yPoints, calculateDominantDirection(xPoints, yPoints)) &&
            calculateStrokeComplexity([[xPoints, yPoints, []]]) > 2;
    };

    const hasOpenCurve = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints, yPoints) && !isCirclePrecise(xPoints, yPoints, calculateStrokeLength(xPoints, yPoints));
    };

    // Helper functions for pattern detection
    const isCircle = (xPoints: number[], yPoints: number[]) => {
        if (xPoints.length < 10) return false;
        const firstPoint = { x: xPoints[0], y: yPoints[0] };
        const lastPoint = { x: xPoints[xPoints.length - 1], y: yPoints[yPoints.length - 1] };
        const distance = Math.sqrt(Math.pow(lastPoint.x - firstPoint.x, 2) + Math.pow(lastPoint.y - firstPoint.y, 2));
        return distance < 30;
    };

    const isVerticalLine = (xPoints: number[], yPoints: number[]) => {
        if (xPoints.length < 3) return false;
        const xVariance = calculateVariance(xPoints);
        const yRange = Math.max(...yPoints) - Math.min(...yPoints);
        return xVariance < 10 && yRange > 20;
    };

    const isHorizontalLine = (xPoints: number[], yPoints: number[]) => {
        if (xPoints.length < 3) return false;
        const yVariance = calculateVariance(yPoints);
        const xRange = Math.max(...xPoints) - Math.min(...xPoints);
        return yVariance < 10 && xRange > 20;
    };

    const calculateVariance = (values: number[]) => {
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    };

    const isSqrtPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for square root symbol pattern
        return hasCheckMark(xPoints, yPoints) && isHorizontalLinePrecise(xPoints, yPoints, calculateDominantDirection(xPoints, yPoints));
    };

    const isIntegralPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for integral symbol pattern
        return hasLongCurve(xPoints, yPoints);
    };

    const isSumPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for summation symbol pattern
        return hasTriangleShape(xPoints, yPoints);
    };

    const isPiPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for pi symbol pattern
        return hasArchWithLegs(xPoints, yPoints);
    };

    const isAlphaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for alpha symbol pattern
        return hasCircleWithTail(xPoints, yPoints);
    };

    const isBetaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for beta symbol pattern
        return hasDoubleLoop(xPoints, yPoints);
    };

    const isDeltaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for delta symbol pattern
        return hasTriangleShape(xPoints, yPoints);
    };

    const isThetaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for theta symbol pattern
        return hasCircleWithLine(xPoints, yPoints);
    };

    const isLambdaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for lambda symbol pattern
        return hasInvertedV(xPoints, yPoints);
    };

    const isSigmaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for sigma symbol pattern
        return hasHorizontalLoop(xPoints, yPoints);
    };

    const isPhiPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for phi symbol pattern
        return hasCircleWithVertical(xPoints, yPoints);
    };

    const isOmegaPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for omega symbol pattern
        return hasOmegaShape(xPoints, yPoints);
    };

    // Additional helper functions for complex patterns
    const hasCheckMark = (xPoints: number[], yPoints: number[]) => {
        // Simplified check for checkmark-like pattern
        return xPoints.length > 5 && yPoints.length > 5;
    };

    const hasLongCurve = (xPoints: number[], yPoints: number[]) => {
        // Check for long curved stroke
        return xPoints.length > 10 && calculateVariance(yPoints) > 100;
    };

    const hasTriangleShape = (xPoints: number[], yPoints: number[]) => {
        // Check for triangle-like shape
        return xPoints.length > 8 && calculateVariance(xPoints) > 50;
    };

    const hasArchWithLegs = (xPoints: number[], yPoints: number[]) => {
        // Check for pi-like arch with legs
        return xPoints.length > 10;
    };

    const hasCircleWithTail = (xPoints: number[], yPoints: number[]) => {
        // Check for alpha-like circle with tail
        return isCircle(xPoints, yPoints) && xPoints.length > 15;
    };

    const hasDoubleLoop = (xPoints: number[], yPoints: number[]) => {
        // Check for double loop pattern
        return xPoints.length > 12 && hasMultipleCurves(xPoints, yPoints);
    };

    const hasCircleWithLine = (xPoints: number[], yPoints: number[]) => {
        // Check for theta-like circle with line
        return isCircle(xPoints, yPoints) && isVerticalLine(xPoints, yPoints);
    };

    const hasInvertedV = (xPoints: number[], yPoints: number[]) => {
        // Check for lambda-like inverted V
        return xPoints.length > 8 && calculateVariance(yPoints) > 50;
    };

    const hasHorizontalLoop = (xPoints: number[], yPoints: number[]) => {
        // Check for sigma-like horizontal loop
        return xPoints.length > 15 && calculateVariance(xPoints) > 100;
    };

    const hasCircleWithVertical = (xPoints: number[], yPoints: number[]) => {
        // Check for phi-like circle with vertical
        return isCircle(xPoints, yPoints) && isVerticalLine(xPoints, yPoints);
    };

    const hasOmegaShape = (xPoints: number[], yPoints: number[]) => {
        // Check for omega-like shape
        return xPoints.length > 20 && hasMultipleCurves(xPoints, yPoints);
    };

    const hasCurve = (xPoints: number[], yPoints: number[]) => {
        return calculateVariance(yPoints) > 20;
    };

    const hasMultipleCurves = (xPoints: number[], yPoints: number[]) => {
        return calculateVariance(yPoints) > 50;
    };

    const hasLineAndAngle = (xPoints: number[], yPoints: number[]) => {
        return isVerticalLine(xPoints, yPoints) && isHorizontalLine(xPoints.slice(-5), yPoints.slice(-5));
    };

    const hasCurveAndLine = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints.slice(0, -5), yPoints.slice(0, -5)) && isHorizontalLinePrecise(xPoints.slice(-5), yPoints.slice(-5), calculateDominantDirection(xPoints.slice(-5), yPoints.slice(-5)));
    };

    const hasLoopAndTail = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints.slice(0, -8), yPoints.slice(0, -8)) && isHorizontalLinePrecise(xPoints.slice(-8), yPoints.slice(-8), calculateDominantDirection(xPoints.slice(-8), yPoints.slice(-8)));
    };

    const hasLoopAndLine = (xPoints: number[], yPoints: number[]) => {
        return hasCurve(xPoints.slice(0, -5), yPoints.slice(0, -5)) && isVerticalLinePrecise(xPoints.slice(-5), yPoints.slice(-5), calculateDominantDirection(xPoints.slice(-5), yPoints.slice(-5)));
    };

    const hasCrossPattern = (xPoints: number[], yPoints: number[]) => {
        // Check for X-like cross pattern
        return hasMultipleDirectionChanges(xPoints, yPoints);
    };

    const hasDownwardStroke = (xPoints: number[], yPoints: number[]) => {
        return yPoints[yPoints.length - 1] > yPoints[0] + 20;
    };

    const hasMultipleDirectionChanges = (xPoints: number[], yPoints: number[]) => {
        let directionChanges = 0;
        for (let i = 1; i < xPoints.length - 1; i++) {
            const prevDir = Math.atan2(yPoints[i] - yPoints[i - 1], xPoints[i] - xPoints[i - 1]);
            const nextDir = Math.atan2(yPoints[i + 1] - yPoints[i], xPoints[i + 1] - xPoints[i]);
            if (Math.abs(prevDir - nextDir) > Math.PI / 4) {
                directionChanges++;
            }
        }
        return directionChanges >= 2;
    };

    const combineRecognitionResults = (results: PromiseSettledResult<any>[]) => {
        const validResults = results
            .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled' && result.value)
            .map(result => result.value);

        if (validResults.length === 0) return null;

        // Sort by confidence and return the best result
        validResults.sort((a, b) => b.confidence - a.confidence);
        return validResults[0].text;
    };

    return (
        <div className="scribble-pad-container" style={{ height: "100%", display: "flex", flexDirection: "column", background: "white" }}>
            <div className="pad-toolbar" style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: "12px", alignItems: "center", background: "#f8fafc" }}>
                <Box sx={{ display: 'flex', gap: 1, p: 0.5, bg: '#f1f5f9', borderRadius: '8px' }}>
                    <IconButton
                        size="small"
                        onClick={() => setMode("draw")}
                        sx={{
                            color: mode === 'draw' ? '#2563eb' : '#64748b',
                            bgcolor: mode === 'draw' ? 'white' : 'transparent',
                            boxShadow: mode === 'draw' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            '&:hover': { bgcolor: mode === 'draw' ? 'white' : '#e2e8f0' }
                        }}
                    >
                        <Pen size={18} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setMode("erase")}
                        sx={{
                            color: mode === 'erase' ? '#2563eb' : '#64748b',
                            bgcolor: mode === 'erase' ? 'white' : 'transparent',
                            boxShadow: mode === 'erase' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            '&:hover': { bgcolor: mode === 'erase' ? 'white' : '#e2e8f0' }
                        }}
                    >
                        <Eraser size={18} />
                    </IconButton>
                </Box>

                <div style={{ width: "1px", height: "24px", background: "#e2e8f0" }}></div>

                <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    style={{ width: "28px", height: "28px", padding: "0", border: "2px solid white", borderRadius: "50%", cursor: "pointer", boxShadow: '0 0 0 1px #e2e8f0' }}
                />

                <select
                    value={lineWidth}
                    onChange={(e) => setLineWidth(Number(e.target.value))}
                    style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: '0.875rem', color: '#475569' }}
                >
                    <option value="2">Thin</option>
                    <option value="5">Medium</option>
                    <option value="10">Thick</option>
                </select>

                <IconButton
                    onClick={clearCanvas}
                    size="small"
                    sx={{ color: '#ef4444', ml: 'auto' }}
                >
                    <Trash2 size={18} />
                </IconButton>
            </div>

            <div className="canvas-wrapper" style={{ flex: 1, position: "relative", cursor: mode === "erase" ? "cell" : "crosshair", overflow: 'hidden' }}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={startDrawing}
                    onMouseUp={stopDrawing}
                    onMouseMove={draw}
                    onMouseOut={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchEnd={stopDrawing}
                    onTouchMove={draw}
                    style={{ position: "absolute", top: 0, left: 0, width: '100%', height: '100%' }}
                />

                {/* Recognition UI */}
                {strokes.length > 0 && (
                    <Box sx={{ position: 'absolute', bottom: 20, right: 20, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {recognizedText && (
                            <Paper elevation={3} sx={{ px: 2, py: 1, borderRadius: '12px', bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0' }}>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Detected:</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2563eb' }}>{recognizedText}</Typography>
                            </Paper>
                        )}
                        <Button
                            variant="contained"
                            startIcon={isRecognizing ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                            onClick={handleRecognize}
                            disabled={isRecognizing}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                bgcolor: '#2563eb',
                                '&:hover': { bgcolor: '#1d4ed8' },
                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                            }}
                        >
                            {isRecognizing ? "Recognizing..." : "Check"}
                        </Button>
                    </Box>
                )}
            </div>
        </div>
    );
};

export default ScribblePad;
